import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Polygon, Tooltip, useMap } from 'react-leaflet';
import { DomEvent, LeafletMouseEvent } from 'leaflet';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { useTheme } from '../../hooks/useTheme';
import { PlannerShape } from '../../types/missionPlanner';
import {
  selectShape,
  removeShape,
  updateShape,
} from '../../store/slices/missionPlannerSlice';
import { shapeVertices, shapeAreaM2, formatArea } from '../../utils/geometry';

interface ShapeLayerProps {
  shape: PlannerShape;
}

/** In-flight body drag: where the grab started and where the shape was. */
interface BodyDrag {
  startLat: number;
  startLng: number;
  originLat: number;
  originLng: number;
}

/**
 * A single coverage shape as a Leaflet Polygon (all kinds are rendered from
 * derived vertices — see utils/geometry). Click selects it; right-click opens
 * a Delete menu; dragging the body moves it (#346). Selection is drawn with a
 * brighter, thicker stroke.
 */
export const ShapeLayer: React.FC<ShapeLayerProps> = ({ shape }) => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const map = useMap();
  const selected = useSelector(
    (state: RootState) => state.missionPlanner.selectedShapeId === shape.id
  );
  // A body drag must never hijack a gesture the operator aimed at the map:
  // drawing a new shape, or dropping waypoints / the home base.
  const mapGestureActive = useSelector(
    (state: RootState) =>
      state.missionPlanner.activeShapeTool !== null ||
      state.missionPlanner.placementMode !== 'idle'
  );
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const drag = useRef<BodyDrag | null>(null);

  // Translate the centre by the pointer delta. Dispatching per move keeps the
  // handles, bounding box and area readout on the shape as it travels; the
  // slice coalesces the stream into one undo entry.
  const onBodyMove = useCallback(
    (e: LeafletMouseEvent) => {
      const grab = drag.current;
      if (!grab) return;
      dispatch(
        updateShape({
          id: shape.id,
          changes: {
            center: {
              lat: grab.originLat + (e.latlng.lat - grab.startLat),
              lng: grab.originLng + (e.latlng.lng - grab.startLng),
            },
          },
        })
      );
    },
    [dispatch, shape.id]
  );

  // Idempotent: unmounting mid-drag, or releasing anywhere, ends the gesture
  // and — critically — hands map panning back.
  const endBodyDrag = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    map.off('mousemove', onBodyMove);
    map.off('mouseup', endBodyDrag);
    document.removeEventListener('mouseup', endBodyDrag);
    map.dragging.enable();
  }, [map, onBodyMove]);

  const startBodyDrag = (e: LeafletMouseEvent) => {
    // Left button only — a right-click belongs to the context menu.
    if (mapGestureActive || (e.originalEvent && e.originalEvent.button !== 0)) {
      return;
    }
    dispatch(selectShape(shape.id));
    drag.current = {
      startLat: e.latlng.lat,
      startLng: e.latlng.lng,
      originLat: shape.center.lat,
      originLng: shape.center.lng,
    };
    map.dragging.disable();
    map.on('mousemove', onBodyMove);
    map.on('mouseup', endBodyDrag);
    // Backstop: the pointer can be released outside the map container, where
    // Leaflet's own mouseup never fires and the drag would otherwise stick.
    document.addEventListener('mouseup', endBodyDrag);
  };

  useEffect(() => endBodyDrag, [endBodyDrag]);

  const positions = useMemo(() => shapeVertices(shape), [shape]);
  const area = formatArea(shapeAreaM2(shape));

  const fill = isDarkMode ? '#4dd0e1' : '#00838f';
  const stroke = selected ? (isDarkMode ? '#ffd54f' : '#f57f17') : fill;

  return (
    <Polygon
      positions={positions}
      color={stroke}
      weight={selected ? 4 : 2}
      fillColor={fill}
      fillOpacity={selected ? 0.25 : 0.15}
      className={`planner-shape planner-shape-${shape.kind}${
        mapGestureActive ? ' planner-shape-locked' : ''
      }`}
      eventHandlers={{
        click: () => dispatch(selectShape(shape.id)),
        mousedown: startBodyDrag,
        // Double-click deletes, mirroring the waypoint idiom (#345, #435).
        // Stop the DOM event so it never reaches the map's dblclick zoom.
        dblclick: (e) => {
          if (e.originalEvent) DomEvent.stop(e.originalEvent);
          endBodyDrag();
          dispatch(removeShape(shape.id));
        },
        contextmenu: (e) => {
          dispatch(selectShape(shape.id));
          const oe = e.originalEvent as MouseEvent;
          oe.preventDefault();
          setMenuPos({ top: oe.clientY, left: oe.clientX });
        },
      }}
    >
      <Tooltip sticky>
        <div>
          <strong style={{ textTransform: 'capitalize' }}>{shape.kind}</strong>
          <br />
          Area: {area}
        </div>
      </Tooltip>

      <Menu
        open={menuPos !== null}
        onClose={() => setMenuPos(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ?? undefined}
      >
        <MenuItem
          onClick={() => {
            dispatch(removeShape(shape.id));
            setMenuPos(null);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          Delete shape
        </MenuItem>
      </Menu>
    </Polygon>
  );
};
